import dayjs from "dayjs"
import { ReportUrl, ScheduledById, SectionScheduldeQuestionnaire } from "../../../../../service/schedule/response"
import { VisitListData } from "../components/visitList/types"
import { Constants } from "../../../../../utils/constants"
import { TFunction } from "react-i18next"
import { BodySection } from "../components/bodySection/types"
import { SectionProps } from "../components/section/types"
import { QuestionProps } from "../components/question/types"
import { AnswerProps } from "../components/answer/types"
import { QuestionOptionType } from "../../../../../utils/enums/optionType"
import { NavigatioStateType } from "../../../../../utils/enums/navigatetionState"
import { deleteDecimalsWithZero, oneHundredAsPercentage } from "../../../../../utils/methods/removeDecimals"
import { TypeOfQuestionarie } from "../../../../../utils/enums/typeQuestionarie"

const formatResponse = ({ response, t }: { response: ScheduledById; t: TFunction<"translation", undefined> }) => {
    const store = response.store
    const management = response.userAssign
    const supervisor = response.userManage
    const timeZone = store?.timeZone ?? Constants.defaultTimeZone
    const visitList: VisitListData = {
        from: response.dateStart ? dayjs(response.dateStart).tz(timeZone).locale('es').format(Constants.dayjsFormat4) : 'N/A',
        to: response.dateEnd ? dayjs(response.dateEnd).tz(timeZone).locale('es').format(Constants.dayjsFormat4) : 'N/A',
        reportUrl: response.reportUrl,
        title: response?.title,
        description: response?.description,
        visitList: response.assignedQuestionnaires
            ?.map(assign => {
                const questionnaireMain = assign?.questionnaireMainData || assign?.questionnaire
                const isScheduleOpen = Boolean(Number(assign?.openScheduled ?? 0))
                const dateStart = assign?.dateStart ? dayjs(assign?.dateStart).tz(timeZone).locale('es').format(Constants.dayjsFormat4) : ''
                const dateEnd = assign?.dateEnd ? dayjs(assign?.dateEnd).tz(timeZone).locale('es').format(Constants.dayjsFormat4) : ''
                return ({
                    id: assign.id?.toString(),
                    title: assign.questionnaireMainData?.name,
                    status: assign?.statusId,
                    result: assign?.score ? `${assign.score}%` : 'Sin resultado',
                    sectionCompleted: `${assign.amountSectionsCompleted}/${questionnaireMain?.sectionCount}`,
                    questonCompleted: `${assign.amountQuestionsAnswered}/${questionnaireMain?.sumQuestions}`,
                    programedType: t(`schedule.scheduleTypes.${isScheduleOpen ? 'free' : 'programmed'}`),
                    dateEnd,
                    detailVersion: {
                        parentId: questionnaireMain?.parentId || questionnaireMain?.id,
                        version: questionnaireMain?.version || '1'
                    },
                    dateStart,
                    sectonList: [],
                    ...assign.reportResultDate && {
                        infoReport: {
                            date: dayjs(assign.reportResultDate).locale('es').format(Constants.dayjsFormat4),
                            url: assign?.reportResultUrl
                        },
                    },
                    ...assign.reportDate && {
                        report: {
                            date: dayjs(assign.reportDate).locale('es').format(Constants.dayjsFormat4),
                            url: assign?.reportUrl
                        },
                    },
                    ...assign.lastDateReportFile && {
                        lastReport: {
                            date: dayjs(assign.lastDateReportFile).locale('es').format(Constants.dayjsFormat4),
                            url: assign?.lastReportFile
                        },
                    }
                })
            }) || [],
    }
    const bodySection: BodySection = {
        store: {
            title: `${store.name} - ${store.keyCode}`,
            description: `${dayjs(store.updateTime).tz(store.timeZone ?? Constants.defaultTimeZone).locale('es').format(Constants.dayjsFormat4)}, ${store.address} ${store.email ?? ''} ${store.phone && store.phone.length > 1 ? `/ ${store.phone}` : ''}`,
            timeZone: store.storeTimeZone?.zone ?? Constants.defaultTimeZoneZone
        },
        title: response?.title,
        description: response?.description,
        management: {
            title: `${management.firstName} ${management.lastName}`,
            userType: `${management.userType.name}`,
            curp: `${management.curp}`,
            role: `${management.role.description}`,
            email: `${management.email}`,
            phone: `${management.phone}`
        },
        supervisor: supervisor.map(supervisor => {
            return ({
                id: `${supervisor.id}`,
                title: `${supervisor.firstName} ${supervisor.lastName}`,
                userType: `${supervisor.userType.name}`,
                curp: `${supervisor.curp}`,
                role: `${supervisor.role.description}`,
                email: `${supervisor.email}`,
                phone: `${supervisor.phone}`
            })
        }),
        reportData: {
            report: {
                date: visitList.from,
                url: visitList.reportUrl,
                type: 1
            }
        },
        status: response?.statusId,
        typeOfQuestionnaire: TypeOfQuestionarie.BYUNITY
    }
    return {
        visitList,
        bodySection
    }
}

const formatScheduleByQuestionnaireResponse = ({ response, header, id }: { id: string | number; response: SectionScheduldeQuestionnaire[]; header: { title: string } }): SectionProps[] => {
    if (!!response?.errors) { return [] }
    const sectionList: SectionProps[] = response?.map((section, index) => {
        return {
            amountQuestions: section.amountQuestions ?? 0,
            description: section.description ?? '',
            statusId: section.statusId ?? 0,
            index,
            pointsOfUser: deleteDecimalsWithZero((section.pointsOfUser ?? 0).toFixed(2)),
            title: section.title ?? '',
            totalPoints: deleteDecimalsWithZero((section.totalPoints ?? 0).toFixed(2)),
            //value: deleteDecimalsWithZero((((section.pointsOfUser || 0) / (section.totalPoints || 1)) * 100).toFixed(2)),
            value: oneHundredAsPercentage( deleteDecimalsWithZero( (((section.pointsOfUser || 0) / (section.totalPoints || 1)) * 100).toFixed(2) ) ),
            questions: (section?.questions?.map((question, index) => {
                const questionFormat: QuestionProps = {
                    index: [index],
                    haveConfigMultiple: {
                        actionPlan: false,
                        evidence: false,
                        reportFolio: false,
                        residenceAlert: false,
                    },
                    position: '1',
                    title: question.title ?? '',
                    type: question.questionTypeId as QuestionOptionType,
                    description: question.description?.toLowerCase() == 'null' ? '' : (question.description ?? ''),
                    textHelp: question.textHelp ?? '',
                    evidenceQuestion: question.evidences ?? [],
                    evidenceMultiple: [{}],
                    answers: (question?.answers?.map((answer, index) => {
                        const answerFormat: Omit<AnswerProps, "type"> & { id: string; } = ({
                            correctOption: !!answer.correctOption,
                            navigateType: NavigatioStateType.SCHEDULE,
                            haveConfig: {
                                actionPlan: !!answer?.actionPlanImplementedId,
                                evidence: !!answer?.evidences?.length,
                                reportFolio: !!answer?.reportFoliosIds,
                                residenceAlert: !!answer?.recurrence,
                            },
                            ids: {
                                actionPlan: answer?.actionPlanImplementedId ?? '',
                                reportFolio: answer?.reportFoliosIds ?? '',
                                residenceAlert: answer?.recurrence ?? '',
                            },
                            header: {
                                ...header ?? {},
                                title: header.title,
                                item: answer.description ?? '',
                                question: question.title ?? '',
                            },
                            id: '1',
                            index: [index],
                            idExecute: id,
                            score: answer.score ?? '0',
                            title: answer.description,
                            action: { label: '', value: '' },
                            evidence: answer.evidences ?? [],
                            iconUrl: answer.iconUrl ?? '',
                            selected: answer.selected,
                        })
                        return answerFormat
                    }) || [])
                }
                return questionFormat
            }) || []),
        }
    })
    return sectionList
}


const formatGenerateReportResponse = ({ response }: { response: ReportUrl }) => {

    const reportUrl: string | undefined = response?.reportUrl

    return reportUrl
}

export { formatResponse, formatScheduleByQuestionnaireResponse, formatGenerateReportResponse }